import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { CSVFile, CSVRow, processCSV } from "@redotech/csv/csv-parser";
import { Currency } from "@redotech/money/currencies";
import {
  ImportRowFormat,
  ITEM_LEVEL_FIELDS,
  OrderImportColumnMapping,
  OrderImportField,
} from "@redotech/redo-fulfillment-common/import-schema";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { ProductInfo } from "@redotech/redo-product-common/product";
import * as XLSX from "xlsx";
import {
  collectParseResults,
  ParseResult,
} from "./validate-fulfillment-orders";

const OZ_TO_GRAMS = 28.3495;

function getField(
  row: CSVRow,
  field: OrderImportField,
  mapping: OrderImportColumnMapping,
): string {
  const columnHeader = mapping.fieldMappings[field];
  if (!columnHeader) {
    return "";
  }
  return row[columnHeader]?.trim() ?? "";
}

function getFieldByIdentifier(
  row: CSVRow,
  field: OrderImportField,
  mapping: OrderImportColumnMapping,
  identifier: string,
): string {
  const template = mapping.fieldMappings[field];
  if (!template) {
    return "";
  }
  const columnHeader = template.replace("{n}", identifier);
  return row[columnHeader]?.trim() ?? "";
}

function discoverItemIdentifiers(
  row: CSVRow,
  mapping: OrderImportColumnMapping,
): string[] {
  const identifiers = new Set<string>();

  for (const field of ITEM_LEVEL_FIELDS) {
    const template = mapping.fieldMappings[field];
    if (!template || !template.includes("{n}")) {
      continue;
    }

    const escaped = template.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = escaped.replace("\\{n\\}", "(.+)");
    const regex = new RegExp(`^${pattern}$`);

    for (const header of Object.keys(row)) {
      const match = header.match(regex);
      if (match?.[1]) {
        identifiers.add(match[1]);
      }
    }
  }

  return Array.from(identifiers);
}

function buildName(
  fullName: string,
  firstName: string,
  lastName: string,
): string {
  if (fullName) {
    return fullName;
  }
  return `${firstName} ${lastName}`.trim();
}

function parseCountryCode(raw: string): RedoShopifyCountryCode {
  if (!raw) {
    return RedoShopifyCountryCode.Us;
  }
  const upper = raw.toUpperCase();
  const found = Object.values(RedoShopifyCountryCode).find(
    (code) => code.toUpperCase() === upper,
  );
  return found ?? RedoShopifyCountryCode.Us;
}

function getPopulatedItemFromSku(sku: string, products: ProductInfo[]) {
  const matchedProduct = products.find((product) =>
    product.variants.some((variant) => variant.sku === sku),
  );
  const matchedVariant = matchedProduct?.variants.find(
    (variant) => variant.sku === sku,
  );
  return { ...matchedProduct, variant: matchedVariant };
}

async function buildLineItem(
  sku: string,
  itemName: string,
  quantityStr: string,
  unitPriceStr: string,
  weightOzStr: string,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<FulfillmentOrderLineItem | null> {
  if (!sku && !itemName) {
    return null;
  }

  const quantity = parseInt(quantityStr, 10);
  if (isNaN(quantity) || quantity <= 0) {
    return null;
  }
  if (quantity > 1000) {
    throw new Error("Failed to parse row - quantity too high");
  }

  const weightOz = parseFloat(weightOzStr) || 0;
  const weightGrams = Math.round(weightOz * OZ_TO_GRAMS);

  let populatedItem: ReturnType<typeof getPopulatedItemFromSku> = {
    variant: undefined,
    variants: [],
  };
  if (sku) {
    const { products } = await searchProducts(sku);
    populatedItem = getPopulatedItemFromSku(sku, products);
  }

  const priceStr = populatedItem?.variant?.price ?? unitPriceStr;
  const unitPriceNum = parseFloat(priceStr) || 0;
  const unitPrice = unitPriceNum.toFixed(2);
  const totalPrice = (unitPriceNum * quantity).toFixed(2);

  return {
    sku,
    quantity,
    unitPrice: { amount: unitPrice, currency: Currency.USD },
    totalPrice: { amount: totalPrice, currency: Currency.USD },
    imageUrl: populatedItem?.image?.url,
    variantId: populatedItem?.variant?.id ?? sku,
    productId: populatedItem?.id ?? sku,
    vendor: "Custom Import",
    requiresShipping: true,
    externalId: sku,
    customAttributes: [],
    totalQuantity: quantity,
    unfulfilledQuantity: quantity,
    weight: {
      unit: WeightUnit.G,
      value: populatedItem?.variant?.weightGrams ?? weightGrams,
    },
    lineItemId: sku || itemName,
    title: populatedItem?.name ?? itemName,
    productTitle: populatedItem?.name ?? itemName,
    variantTitle: populatedItem?.variant?.name ?? itemName,
    unitDiscountedPrice: { amount: unitPrice, currency: Currency.USD },
    totalDiscountedPrice: { amount: totalPrice, currency: Currency.USD },
    totalTax: { amount: "0", currency: Currency.USD },
    hsCode: null,
  };
}

async function buildOrderFromRows(
  rows: CSVRow[],
  mapping: OrderImportColumnMapping,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<CreateFulfillmentOrder> {
  const firstRow = rows[0];
  if (!firstRow) {
    throw new Error("Failed to parse order - no rows found");
  }

  const fullName = getField(
    firstRow,
    OrderImportField.RECIPIENT_FULL_NAME,
    mapping,
  );
  const firstName = getField(
    firstRow,
    OrderImportField.RECIPIENT_FIRST_NAME,
    mapping,
  );
  const lastName = getField(
    firstRow,
    OrderImportField.RECIPIENT_LAST_NAME,
    mapping,
  );
  const name = buildName(fullName, firstName, lastName);

  const email = getField(firstRow, OrderImportField.BUYER_EMAIL, mapping);
  const phone = getField(firstRow, OrderImportField.BUYER_PHONE, mapping);
  const company = getField(
    firstRow,
    OrderImportField.RECIPIENT_COMPANY,
    mapping,
  );
  const address1 = getField(firstRow, OrderImportField.ADDRESS_LINE_1, mapping);
  const address2 = getField(firstRow, OrderImportField.ADDRESS_LINE_2, mapping);
  const city = getField(firstRow, OrderImportField.CITY, mapping);
  const state = getField(firstRow, OrderImportField.STATE, mapping);
  const zip = getField(firstRow, OrderImportField.POSTAL_CODE, mapping);
  const countryCodeRaw = getField(
    firstRow,
    OrderImportField.COUNTRY_CODE,
    mapping,
  );
  const countryCode = parseCountryCode(countryCodeRaw);
  const orderNumber = getField(
    firstRow,
    OrderImportField.ORDER_NUMBER,
    mapping,
  );
  const internalNotes = getField(
    firstRow,
    OrderImportField.INTERNAL_NOTES,
    mapping,
  );
  const noteToBuyer = getField(
    firstRow,
    OrderImportField.NOTES_TO_BUYER,
    mapping,
  );
  const giftMessage = getField(
    firstRow,
    OrderImportField.GIFT_MESSAGE,
    mapping,
  );
  const orderDateStr = getField(firstRow, OrderImportField.ORDER_DATE, mapping);
  const paidDateStr = getField(firstRow, OrderImportField.DATE_PAID, mapping);

  if (!name || !address1 || !city || !state || !zip) {
    throw new Error("Failed to parse row - missing required address fields");
  }

  const orderDate = orderDateStr ? new Date(orderDateStr) : new Date();
  const paidDate = paidDateStr ? new Date(paidDateStr) : new Date();

  // Build line items from all rows (one per line item in MULTIPLE_ROWS_PER_ORDER)
  const items: FulfillmentOrderLineItem[] = (
    await Promise.all(
      rows.map(async (row) => {
        const sku = getField(row, OrderImportField.ITEM_SKU, mapping);
        const itemName = getField(row, OrderImportField.ITEM_NAME, mapping);
        const quantityStr = getField(
          row,
          OrderImportField.ITEM_QUANTITY,
          mapping,
        );
        const unitPriceStr = getField(
          row,
          OrderImportField.ITEM_UNIT_PRICE,
          mapping,
        );
        const weightOzStr = getField(
          row,
          OrderImportField.ITEM_WEIGHT_OZ,
          mapping,
        );

        try {
          return await buildLineItem(
            sku,
            itemName,
            quantityStr,
            unitPriceStr,
            weightOzStr,
            searchProducts,
          );
        } catch (error) {
          console.error(error);
          return null;
        }
      }),
    )
  ).filter((item): item is FulfillmentOrderLineItem => item !== null);

  const orderData: CreateFulfillmentOrder = {
    address: {
      countryCode,
      name,
      email: email || undefined,
      address1,
      address2: address2 || undefined,
      city,
      province: state,
      zip,
      phone: phone || undefined,
      company: company || undefined,
    },
    orderInfo: {
      orderNumber,
      orderDate,
      paidDate,
      shippingPaid: { amount: "0", currency: Currency.USD },
      taxPaid: { amount: "0", currency: Currency.USD },
      totalPaid: { amount: "0", currency: Currency.USD },
      items,
      internalNote: internalNotes,
      noteToBuyer: giftMessage || noteToBuyer,
    },
  };

  return orderData;
}

async function buildOrderFromSingleRow(
  row: CSVRow,
  mapping: OrderImportColumnMapping,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<CreateFulfillmentOrder> {
  const fullName = getField(row, OrderImportField.RECIPIENT_FULL_NAME, mapping);
  const firstName = getField(
    row,
    OrderImportField.RECIPIENT_FIRST_NAME,
    mapping,
  );
  const lastName = getField(row, OrderImportField.RECIPIENT_LAST_NAME, mapping);
  const name = buildName(fullName, firstName, lastName);

  const email = getField(row, OrderImportField.BUYER_EMAIL, mapping);
  const phone = getField(row, OrderImportField.BUYER_PHONE, mapping);
  const company = getField(row, OrderImportField.RECIPIENT_COMPANY, mapping);
  const address1 = getField(row, OrderImportField.ADDRESS_LINE_1, mapping);
  const address2 = getField(row, OrderImportField.ADDRESS_LINE_2, mapping);
  const city = getField(row, OrderImportField.CITY, mapping);
  const state = getField(row, OrderImportField.STATE, mapping);
  const zip = getField(row, OrderImportField.POSTAL_CODE, mapping);
  const countryCodeRaw = getField(row, OrderImportField.COUNTRY_CODE, mapping);
  const countryCode = parseCountryCode(countryCodeRaw);
  const orderNumber = getField(row, OrderImportField.ORDER_NUMBER, mapping);
  const internalNotes = getField(row, OrderImportField.INTERNAL_NOTES, mapping);
  const noteToBuyer = getField(row, OrderImportField.NOTES_TO_BUYER, mapping);
  const giftMessage = getField(row, OrderImportField.GIFT_MESSAGE, mapping);
  const orderDateStr = getField(row, OrderImportField.ORDER_DATE, mapping);
  const paidDateStr = getField(row, OrderImportField.DATE_PAID, mapping);

  if (!name || !address1 || !city || !state || !zip) {
    throw new Error("Failed to parse row - missing required address fields");
  }

  const orderDate = orderDateStr ? new Date(orderDateStr) : new Date();
  const paidDate = paidDateStr ? new Date(paidDateStr) : new Date();

  const identifiers = discoverItemIdentifiers(row, mapping);
  const items: FulfillmentOrderLineItem[] = [];

  for (const id of identifiers) {
    const sku = getFieldByIdentifier(
      row,
      OrderImportField.ITEM_SKU,
      mapping,
      id,
    );
    const itemName = getFieldByIdentifier(
      row,
      OrderImportField.ITEM_NAME,
      mapping,
      id,
    );
    const quantityStr = getFieldByIdentifier(
      row,
      OrderImportField.ITEM_QUANTITY,
      mapping,
      id,
    );
    const unitPriceStr = getFieldByIdentifier(
      row,
      OrderImportField.ITEM_UNIT_PRICE,
      mapping,
      id,
    );
    const weightOzStr = getFieldByIdentifier(
      row,
      OrderImportField.ITEM_WEIGHT_OZ,
      mapping,
      id,
    );

    if (!sku && !itemName) {
      continue;
    }

    try {
      const item = await buildLineItem(
        sku,
        itemName,
        quantityStr,
        unitPriceStr,
        weightOzStr,
        searchProducts,
      );
      if (item) {
        items.push(item);
      }
    } catch (error) {
      console.error(error);
    }
  }

  const orderData: CreateFulfillmentOrder = {
    address: {
      countryCode,
      name,
      email: email || undefined,
      address1,
      address2: address2 || undefined,
      city,
      province: state,
      zip,
      phone: phone || undefined,
      company: company || undefined,
    },
    orderInfo: {
      orderNumber,
      orderDate,
      paidDate,
      shippingPaid: { amount: "0", currency: Currency.USD },
      taxPaid: { amount: "0", currency: Currency.USD },
      totalPaid: { amount: "0", currency: Currency.USD },
      items,
      internalNote: internalNotes,
      noteToBuyer: giftMessage || noteToBuyer,
    },
  };

  return orderData;
}

async function processSpreadsheetFile(file: File): Promise<CSVFile> {
  const ext = file.name.toLowerCase().split(".").pop();

  if (ext === "xls" || ext === "xlsx") {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      throw new Error("No sheets found in workbook");
    }
    const worksheet = workbook.Sheets[firstSheetName];
    if (!worksheet) {
      throw new Error("Could not read worksheet");
    }

    // Detect and skip a title row: if the first row has only one non-empty
    // cell while the second row has many, the first row is a label.
    const range = XLSX.utils.decode_range(worksheet["!ref"] ?? "A1");
    let dataStart = range.s.r;
    if (range.e.r > range.s.r) {
      let firstRowCells = 0;
      let secondRowCells = 0;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const firstCell =
          worksheet[XLSX.utils.encode_cell({ r: range.s.r, c })];
        const secondCell =
          worksheet[XLSX.utils.encode_cell({ r: range.s.r + 1, c })];
        if (firstCell && firstCell.v !== undefined && firstCell.v !== "") {
          firstRowCells++;
        }
        if (secondCell && secondCell.v !== undefined && secondCell.v !== "") {
          secondRowCells++;
        }
      }
      if (firstRowCells <= 1 && secondRowCells > 1) {
        dataStart = range.s.r + 1;
      }
    }

    const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(
      worksheet,
      { defval: "", range: dataStart },
    );
    const rows: CSVRow[] = jsonData.map((row) => {
      const stringRow: CSVRow = {};
      for (const [key, value] of Object.entries(row)) {
        stringRow[key] = value != null ? String(value) : "";
      }
      return stringRow;
    });
    return { rows };
  }

  return processCSV(file);
}

export async function parseCustomImportCsv(
  file: File,
  mapping: OrderImportColumnMapping,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<ParseResult> {
  const data: CSVFile = await processSpreadsheetFile(file);
  const orderNumberField = mapping.fieldMappings[OrderImportField.ORDER_NUMBER];

  const getOrderNumber = (row: CSVRow, i: number) =>
    orderNumberField
      ? row[orderNumberField]?.trim() || `Row ${i + 1}`
      : `Row ${i + 1}`;

  if (mapping.rowFormat === ImportRowFormat.MULTIPLE_ROWS_PER_ORDER) {
    const orderGroups = new Map<string, CSVRow[]>();

    for (const row of data.rows) {
      const orderNumber = orderNumberField
        ? (row[orderNumberField]?.trim() ?? "")
        : "";
      if (!orderNumber) {
        continue;
      }
      const existing = orderGroups.get(orderNumber) ?? [];
      existing.push(row);
      orderGroups.set(orderNumber, existing);
    }

    const entries = Array.from(orderGroups.entries());

    return collectParseResults(
      entries,
      async ([, rows]) => buildOrderFromRows(rows, mapping, searchProducts),
      ([orderNumber]) => orderNumber,
    );
  }

  // SINGLE_ROW_WITH_NUMBERED_COLUMNS: each row is one order
  return collectParseResults(
    data.rows,
    async (row) => buildOrderFromSingleRow(row, mapping, searchProducts),
    getOrderNumber,
  );
}

// Verify no item-level fields appear as non-template columns in SINGLE_ROW_WITH_NUMBERED_COLUMNS
export function validateCustomImportMapping(
  mapping: OrderImportColumnMapping,
): string[] {
  const errors: string[] = [];

  if (mapping.rowFormat === ImportRowFormat.SINGLE_ROW_WITH_NUMBERED_COLUMNS) {
    for (const field of Object.values(OrderImportField)) {
      const columnHeader = mapping.fieldMappings[field];
      if (
        ITEM_LEVEL_FIELDS.has(field) &&
        columnHeader &&
        !columnHeader.includes("{n}")
      ) {
        errors.push(
          `Item field "${field}" must use {n} template in SINGLE_ROW_WITH_NUMBERED_COLUMNS format`,
        );
      }
    }
  }

  return errors;
}
